/**
 * components/doodle-canvas.tsx
 *
 * Finger-draw canvas for love note doodles.
 * Uses pointer events for cross-device drawing (touch + mouse).
 *
 * Props:
 *   onDone(blob)   — called with PNG Blob when user taps "Done"
 *   onCancel()     — called when user taps "Cancel"
 */
import { useRef, useEffect, useState, useCallback } from 'react'

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const COLORS = [
  { value: '#D4537E', label: 'Pink' },
  { value: '#2C1810', label: 'Dark' },
  { value: '#7C5CBF', label: 'Purple' },
  { value: '#5B8A5E', label: 'Green' },
  { value: '#D4823A', label: 'Orange' },
  { value: '#FFFFFF', label: 'White' },
] as const

const BRUSH_SIZES = [
  { value: 2, label: 'S' },
  { value: 5, label: 'M' },
  { value: 10, label: 'L' },
] as const

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface DoodleCanvasProps {
  onDone: (blob: Blob) => void
  onCancel: () => void
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function DoodleCanvas({ onDone, onCancel }: DoodleCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const isDrawingRef = useRef(false)
  const lastPosRef = useRef<{ x: number; y: number } | null>(null)

  const [color, setColor] = useState<string>('#D4537E')
  const [brushSize, setBrushSize] = useState<number>(5)
  const [hasStrokes, setHasStrokes] = useState(false)

  // Set up canvas on mount
  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    // Fill with cream background
    ctx.fillStyle = '#FAF8F4'
    ctx.fillRect(0, 0, canvas.width, canvas.height)
  }, [])

  // Get canvas-relative coordinates from a pointer event
  const getPos = useCallback(
    (e: React.PointerEvent<HTMLCanvasElement>): { x: number; y: number } => {
      const canvas = canvasRef.current!
      const rect = canvas.getBoundingClientRect()
      const scaleX = canvas.width / rect.width
      const scaleY = canvas.height / rect.height
      return {
        x: (e.clientX - rect.left) * scaleX,
        y: (e.clientY - rect.top) * scaleY,
      }
    },
    [],
  )

  const startDrawing = useCallback(
    (e: React.PointerEvent<HTMLCanvasElement>) => {
      e.preventDefault()
      const canvas = canvasRef.current
      if (!canvas) return
      canvas.setPointerCapture(e.pointerId)
      isDrawingRef.current = true
      lastPosRef.current = getPos(e)
    },
    [getPos],
  )

  const draw = useCallback(
    (e: React.PointerEvent<HTMLCanvasElement>) => {
      e.preventDefault()
      if (!isDrawingRef.current) return
      const canvas = canvasRef.current
      if (!canvas) return
      const ctx = canvas.getContext('2d')
      if (!ctx) return

      const pos = getPos(e)
      const from = lastPosRef.current ?? pos

      ctx.beginPath()
      ctx.moveTo(from.x, from.y)
      ctx.lineTo(pos.x, pos.y)
      ctx.strokeStyle = color
      ctx.lineWidth = brushSize
      ctx.lineCap = 'round'
      ctx.lineJoin = 'round'
      ctx.stroke()

      lastPosRef.current = pos
      setHasStrokes(true)
    },
    [color, brushSize, getPos],
  )

  const stopDrawing = useCallback((e: React.PointerEvent<HTMLCanvasElement>) => {
    e.preventDefault()
    isDrawingRef.current = false
    lastPosRef.current = null
  }, [])

  const handleClear = () => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    ctx.fillStyle = '#FAF8F4'
    ctx.fillRect(0, 0, canvas.width, canvas.height)
    setHasStrokes(false)
  }

  const handleDone = () => {
    const canvas = canvasRef.current
    if (!canvas) return
    canvas.toBlob(
      (blob) => {
        if (blob) onDone(blob)
      },
      'image/png',
    )
  }

  return (
    <div className="flex flex-col gap-3">
      {/* Canvas */}
      <div className="rounded-[16px] border-2 border-[#F4C0D1] overflow-hidden shadow-[0_2px_12px_rgba(212,83,126,0.08)]">
        <canvas
          ref={canvasRef}
          width={600}
          height={400}
          className="w-full touch-none block"
          style={{ background: '#FAF8F4', cursor: 'crosshair' }}
          onPointerDown={startDrawing}
          onPointerMove={draw}
          onPointerUp={stopDrawing}
          onPointerCancel={stopDrawing}
        />
      </div>

      {/* Color picker */}
      <div className="flex items-center gap-2 px-1 overflow-x-auto scrollbar-none">
        <span className="font-['DM_Sans'] text-[11px] text-[#B89AA2] shrink-0">Color</span>
        <div className="flex gap-[10px] ml-1">
          {COLORS.map((c) => (
            <button
              key={c.value}
              type="button"
              aria-label={c.label}
              onClick={() => setColor(c.value)}
              className="w-[28px] h-[28px] rounded-full border-2 transition-all duration-150 shrink-0 active:scale-90"
              style={{
                backgroundColor: c.value,
                borderColor: color === c.value ? '#D4537E' : '#F4C0D1',
                boxShadow: color === c.value ? '0 0 0 2px #D4537E' : 'none',
              }}
            />
          ))}
        </div>
      </div>

      {/* Brush size */}
      <div className="flex items-center gap-2 px-1">
        <span className="font-['DM_Sans'] text-[11px] text-[#B89AA2] shrink-0">Size</span>
        <div className="flex gap-2 ml-1">
          {BRUSH_SIZES.map((b) => (
            <button
              key={b.value}
              type="button"
              onClick={() => setBrushSize(b.value)}
              className="w-[36px] h-[28px] rounded-[8px] border transition-all duration-150 font-['DM_Sans'] text-[12px] font-medium active:scale-95"
              style={{
                borderColor: brushSize === b.value ? '#D4537E' : '#F4C0D1',
                background: brushSize === b.value ? '#FBEAF0' : '#FFFFFF',
                color: brushSize === b.value ? '#D4537E' : '#B89AA2',
              }}
            >
              {b.label}
            </button>
          ))}
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-2 pt-1">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 h-[44px] rounded-[12px] border border-[#F4C0D1] bg-white font-['DM_Sans'] text-[14px] font-medium text-[#B89AA2] active:scale-[0.97] transition-all duration-150"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleClear}
          disabled={!hasStrokes}
          className="h-[44px] px-4 rounded-[12px] border border-[#F4C0D1] bg-white font-['DM_Sans'] text-[14px] font-medium text-[#B89AA2] active:scale-[0.97] transition-all duration-150 disabled:opacity-40"
        >
          Clear
        </button>
        <button
          type="button"
          onClick={handleDone}
          className="flex-1 h-[44px] rounded-[12px] bg-[#D4537E] font-['DM_Sans'] text-[14px] font-medium text-white shadow-[0_4px_16px_rgba(212,83,126,0.28)] active:scale-[0.97] active:bg-[#993556] transition-all duration-150"
        >
          Use doodle
        </button>
      </div>
    </div>
  )
}
